import { useEffect, useRef, useState } from "react";
import { MatchStorage, MatchPlayer } from "../services/MatchStorage";
import { WebSocketTransport } from "../services/WebSocketTransport";
import { claimAiDriver, releaseAiDriver } from "../../shared/api/socket";

// Pause between a bot's prompt arriving and its answer, so bots read as players rather than reflexes
const AI_THINK_MS: [number, number] = [500, 1500];

/** What a game bundle's createClientTrustMatchViewer hands back (older bundles return nothing). */
type ClientTrustController = {
  setAiEnabled?: (enabled: boolean) => void;
  unmount?: () => void;
};

type ExecutionMode = "client" | "server";

type GameModuleMountProps = {
  module: any;
  matchId: string;
  matchStorage: MatchStorage | null; // Required for client mode, null for server mode
  userId: string;
  players: MatchPlayer[];
  executionMode: ExecutionMode;
  gameOptions?: Record<string, string | string[]>;
  onLoaded: () => void;
  onLoadProgress: (totalPanels: number, panelsLoaded: number) => void;
};

/**
 * WARNING: This component uses direct DOM manipulation.
 *
 * This is INTENTIONAL and REQUIRED. We hand off a DOM element to an externally-loaded
 * game module that manages its own rendering completely outside of React's control.
 *
 * DO NOT attempt to:
 * - Manage this element's contents through React state
 * - Add React children to the container
 * - Use React refs to manipulate the game's internal DOM
 *
 * The game module takes full ownership of the container element and its contents.
 * React's only job here is to provide the container and manage its lifecycle.
 */
export function GameModuleMount({
  module,
  matchId,
  matchStorage,
  userId,
  players,
  executionMode,
  gameOptions,
  onLoaded,
  onLoadProgress,
}: GameModuleMountProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(false);
  // The client-trust viewer's controls, once created. The AI driver lease loop hangs off this.
  const [controller, setController] = useState<ClientTrustController | undefined>();
  // Store callbacks in refs to avoid re-running effect when they change
  const onLoadedRef = useRef(onLoaded);
  const onLoadProgressRef = useRef(onLoadProgress);
  onLoadedRef.current = onLoaded;
  onLoadProgressRef.current = onLoadProgress;

  useEffect(() => {
    // For client mode, we need matchStorage; for server mode, we don't
    if (!module || !players || !userId || mountedRef.current) {
      return;
    }
    if (executionMode === "client" && !matchStorage) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    mountedRef.current = true;
    container.style.display = "block";

    const loadTimeout = setTimeout(() => onLoadedRef.current(), 250);

    // Determine if we should show the exit button (not in Discord context)
    const showExitButton = document.location.href.indexOf("discord") < 0;
    const onBack = showExitButton
      ? () => {
          document.location.href = "/spa/match";
        }
      : undefined;

    const handleLoadProgress = (totalPanels: number, panelsLoaded: number) => {
      clearTimeout(loadTimeout);
      onLoadProgressRef.current(totalPanels, panelsLoaded);
    };

    if (executionMode === "server") {
      // Server-authoritative mode: WebSocketTransport handles connection and reconnection
      (async () => {
        try {
          const transport = await WebSocketTransport.create(matchId);

          module.createServerTrustMatchViewer(
            container,
            userId,
            transport,
            onBack,
            handleLoadProgress,
          );
        } catch (error) {
          console.error("[GameModuleMount] Failed to connect:", error);
        }
      })();
    } else {
      // Client-trust mode: use matchStorage directly.
      // Every tab runs its own copy of the engine, so bots start paused; the lease effect below
      // lets them answer only in the tab holding the AI driver lease.
      const created: ClientTrustController | undefined = module.createClientTrustMatchViewer(
        container,
        matchStorage,
        userId,
        players,
        onBack,
        handleLoadProgress,
        gameOptions ?? {},
        { seat: { thinkMs: AI_THINK_MS }, enabled: false },
      );
      setController(created);
    }
  }, [module, matchStorage, matchId, players, userId, executionMode, gameOptions]);

  // AI driver lease (client-trust only): claim it, renew it on a timer, and give it back on cleanup.
  // This lives in its own effect so start and stop always pair up. The viewer is created once
  // (mountedRef), so a loop tied to that effect would be stopped by React StrictMode's simulated
  // unmount and never restarted, leaving every bot paused.
  const hasAiSeats = !!players?.some((p) => p.ai);
  useEffect(() => {
    const setAiEnabled = controller?.setAiEnabled;
    if (!setAiEnabled || !hasAiSeats) {
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let driving = false;

    const renew = async () => {
      if (cancelled) return;
      const { driver, renewMs } = await claimAiDriver(matchId);
      if (cancelled) return;
      if (driver !== driving) {
        driving = driver;
        console.log(`[GameModuleMount] AI driver for ${matchId}: ${driver ? "this tab" : "another tab"}`);
        setAiEnabled(driver);
      }
      timer = setTimeout(renew, renewMs);
    };
    renew();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (driving) {
        setAiEnabled(false);
      }
      releaseAiDriver(matchId);
    };
  }, [controller, hasAiSeats, matchId]);

  return (
    <div
      id="player"
      ref={containerRef}
      style={{
        display: "none",
        width: "100%",
        height: "100%",
      }}
    />
  );
}
